import { useState } from 'react'
import {
  CloseButton,
  Input,
  NativeSelect,
  Button,
  VStack,
  HStack,
  Alert,
  Text,
  InputGroup,
  IconButton,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiCopy } from 'react-icons/fi'
import { useIssueInvite } from '../../hooks/useInvites'
import { useCommitteeOptions } from '../../hooks/useAdminUsers'
import { ResponsiveModal } from '../shared'
import type { IssuedInvite } from '../../types/invites'
import { publicOrigin } from '../../lib/native/instance'

interface IssueInviteModalProps {
  isOpen: boolean
  onClose: () => void
}

export function IssueInviteModal({ isOpen, onClose }: IssueInviteModalProps) {
  const issueInvite = useIssueInvite()
  const { data: committeeOptions } = useCommitteeOptions()

  const [email, setEmail] = useState('')
  const [role, setRole] = useState('Organizer')
  const [committeeId, setCommitteeId] = useState('')
  const [expiresInDays, setExpiresInDays] = useState('')
  const [issued, setIssued] = useState<IssuedInvite | null>(null)

  const handleClose = () => {
    setEmail('')
    setRole('Organizer')
    setCommitteeId('')
    setExpiresInDays('')
    setIssued(null)
    onClose()
  }

  const handleSubmit = async () => {
    try {
      const result = await issueInvite.mutateAsync({
        email,
        role,
        committeeId: committeeId || undefined,
        expiresInDays: expiresInDays ? Number(expiresInDays) : undefined,
      })
      setIssued(result)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to issue invite'
      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
        duration: 5000,
      })
    }
  }

  // [Native B1] The instance's origin; null on native with no instance → no link, copy disabled.
  const origin = publicOrigin()
  const acceptUrl = issued && origin ? `${origin}${issued.acceptUrlPath}` : ''

  const copyLink = () => {
    navigator.clipboard.writeText(acceptUrl)
    toaster.create({ type: 'success', title: 'Invite link copied' })
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Invite User</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            {issued ? (
              <VStack gap={4} align="stretch">
                <Alert.Root status="success" borderRadius="md">
                  <Alert.Indicator />
                  Invite created. This link is shown only once — copy it now.
                </Alert.Root>
                {issued.emailStatus === 'Sent' ? (
                  <Alert.Root status="success" borderRadius="md">
                    <Alert.Indicator />
                    Invite emailed to {issued.email}
                  </Alert.Root>
                ) : (
                  <Alert.Root status="warning" borderRadius="md">
                    <Alert.Indicator />
                    Email not sent — copy the link below
                  </Alert.Root>
                )}
                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Accept link</Field.Label>
                  <InputGroup endElement={
                    <IconButton
                      aria-label="Copy invite link"
                      size="sm"
                      variant="ghost"
                      disabled={!acceptUrl}
                      onClick={copyLink}><FiCopy /></IconButton>
                  }>
                    <Input size="sm" value={acceptUrl} readOnly />
                  </InputGroup>
                </Field.Root>
                <Text fontSize="sm" color="gray.500">
                  Expires {new Date(issued.expiresAt).toLocaleString()}
                </Text>
              </VStack>
            ) : (
              <VStack gap={4}>
                {!!issueInvite.error && (
                  <Alert.Root status="error" borderRadius="md">
                    <Alert.Indicator />
                    Failed to issue invite. Please try again.
                  </Alert.Root>
                )}

                <Field.Root required>
                  <Field.Label fontSize="sm" color="gray.600">Email</Field.Label>
                  <Input
                    size="sm"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Role</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field value={role} onChange={(e) => setRole(e.target.value)}>
                      <option value="Organizer">Organizer</option>
                      <option value="Admin">Admin</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Committee</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      value={committeeId}
                      onChange={(e) => setCommitteeId(e.target.value)}
                      placeholder="Unassigned">
                      {committeeOptions?.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Expires in (days)</Field.Label>
                  <Input
                    size="sm"
                    type="number"
                    min={1}
                    placeholder="Default"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                  />
                </Field.Root>
              </VStack>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <HStack gap={3}>
              {issued ? (
                <Button colorPalette="brand" size="sm" onClick={handleClose}>
                  Done
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="brand"
                    size="sm"
                    onClick={handleSubmit}
                    loading={issueInvite.isPending}
                    disabled={!email}
                  >
                    Send Invite
                  </Button>
                </>
              )}
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
